import { call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import moment from 'moment';
import { ConsolidatedPlacementTargetEndpoints } from 'app/services/PlacementServices/utils';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { applyDataToTemplate, GetTemplatesByCategory } from 'app/services/NotificationServices/TemplateService';
import { PlacementModsService } from 'app/services/PlacementModsServices/placementmods-service';
import { getOrderDetailsById } from 'app/services/OrderServices/OrderServices';
import {
  getApprovalVariantEmailSubstitutions,
  getLatestSentDateForClientConfirmation,
  getProcessedSplits,
} from '@AMIEWEB/MarginTool/emailHelper';
import { sortAssignmentSplits } from '@AMIEWEB/MarginTool/Components/Assignments/helper';
import { searchNotificationData, signatureSubstitutionToken } from '@AMIEWEB/GlobalSearch/helper';
import {
  formatEmailRecipientsList,
  formatManualRecipientList,
  getApprovalEmailSubject,
  getCandidateDetails,
  getCandidateEmailSubject,
  getReimbursement,
  getSplitData,
  getSubstitutions,
  getSubstitutionValues,
} from '@AMIEWEB/MarginTool/Components/PayPackage/helper';
import { EmailVariant, MarginTemplateTitle } from '@AMIEWEB/MarginTool/Components/PayPackage/PayPackageHelper';
import { BonusFields } from '@AMIEWEB/MarginTool/Components/PayPackage/enum';
import { formattedNumber } from '@AMIEWEB/MarginTool/Components/PayPackage/PayPackagePreview/helper';
import { IEmailType, IEmployee, InitiationPoints } from 'app/models/Notification/Notification';
import { Concatenate } from 'utils/string/string';
import { ChannelType, SendType, UseSubType, UseType } from '@AMIEWEB/Notification/Constants';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import { EmailDefaultValues } from 'app/models/Email/IEmail';
import { placementMarginPayPackageStatusActions } from '../placement-pay-package-status/placement-pay-package-status.redux';
import { placementMarginToolEmailAction } from './placement-margin-tool-email.redux';
import { httpSuccess } from 'app/services/serviceHelpers';
import { selectUser } from 'oidc/user.selectors';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import {
  selectPlacementMarginEmailRecipientsList,
  selectPlacementMarginStatusUpdatedEmailModalOpen,
} from '../placement-pay-package-status/placement-pay-package-status.selector';
import {
  selectPlacementMarginBookingPeriod,
  selectPlacementMarginToolDetailsData,
  selectSelectedPlacementMarginScenario,
} from '../placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { getDefaultSender } from '@AMIEWEB/Candidate/CandidateProfile/CandidateHeader/NotificationHelper';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { manuallyIncrementPromiseCounter, manuallyResetPromiseCounter, trackPromise } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { findMarginDetailByBookingPeriodId } from '@AMIEWEB/MarginTool/Components/PayPackage/AddScenario/helper';
import { selectPlacementMarginOverTimeHourlyRates } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { IPlacementMarginOverTimeHourlyRateItem } from '../placement-add-edit-scenario/add-edit-placement-scenario.model';

import splitTemplate from './templates/splitTemplate.hbs';
import reimbursementTemplate from './templates/reimbursementTemplate.hbs';
import approvalEmailSplitTemplate from './templates/approvalEmailSplitTemplate.hbs';

export const TrackMarginToolTemplate = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.marginToolTemplate);
export const TrackFetchplacementHeader = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.fetchplacementHeader);
export const TrackFetchOrderDetails = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.fetchOrderDetails);
export const TrackFetchPlacementDocs = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.fetchPlacementDocs);
export const TrackFetchBillRateMod = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.fetchBillRateMod);
export const TrackMarginToolPreview = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.marginToolPreview);

function* handlePlacementMarginToolEmailCreation() {
  try {
    const user = yield select(selectUser);
    const userPreference = yield select(selectUserPreference);
    const selectEmailRecipients = yield select(selectPlacementMarginEmailRecipientsList);
    const headerData = yield select(selectSelectedPlacementMarginScenario);
    const marginData = yield select(selectPlacementMarginToolDetailsData);
    const bookingData = yield select(selectPlacementMarginBookingPeriod);
    const foundPlacement = yield select(selectPlacementDetails);
    const isStatusUpdatedEmailModalOpen = yield select(selectPlacementMarginStatusUpdatedEmailModalOpen);
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    const existingRates: IPlacementMarginOverTimeHourlyRateItem[] = yield select(
      selectPlacementMarginOverTimeHourlyRates,
    );
    const sender: IEmployee = getDefaultSender(user);
    const statusId = headerData?.scenarioStatusId;
    const orderId = bookingData?.orderId;
    const placementId = headerData?.placementId;
    const placementService: PlacementService = yield getContext('placementService');
    const placementModService: PlacementModsService = yield getContext('placementModsService');

    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolTemplate);
    const templates = yield call(TrackMarginToolTemplate, GetTemplatesByCategory, 'Margin Tool', 'email');
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolTemplate);

    const isCandidateEmail = isStatusUpdatedEmailModalOpen?.emailVariant === EmailVariant.Candidate;
    const currentMarginData = findMarginDetailByBookingPeriodId(marginData, selectedScenario?.bookingPeriodId);

    let body = '';
    let useType = UseType.General;
    let subject = '';
    let templateSubstitutions = [];
    if (isCandidateEmail) {
      const payQuoteTemplate = templates?.filter(data => data.title === MarginTemplateTitle.CandidatePayQuote);
      const splitData = getSplitData(
        headerData?.splits,
        headerData?.tla,
        existingRates,
        headerData?.assignment?.payPackageDetailsValues,
      );
      const payPackageDetailsValues = headerData?.assignment?.payPackageDetailsValues ?? [];
      const reimbursementData = getReimbursement(payPackageDetailsValues, bookingData?.brand);

      // Facility bonuses are "double-taxed"; candidate-facing quote should reflect amount - 20%
      const adjustedPickListAmountsByLabel = payPackageDetailsValues?.reduce((acc, item) => {
        const label = item?.pickListDescription;
        const amount = item?.payPackageValues?.amount;
        if (!label || !amount) return acc;
        const isExcluded = bookingData?.brand === 'AMN Allied' && label === 'Miscellaneous Medical or Lab Work';
        if (isExcluded) return acc;
        const prev = acc.get(label) ?? 0;
        acc.set(label, prev + amount);
        return acc;
      }, new Map<string, number>());

      const adjustedReimbursementData = reimbursementData?.map(item => {
        const label = item?.pickListLabel;
        if (
          label === BonusFields.facilityCompletion ||
          label === BonusFields.facilitySignOn
        ) {
          const raw = adjustedPickListAmountsByLabel?.get(label) ?? 0;
          const adjusted = raw * 0.8;
          return {
            ...item,
            pickListValue: adjusted ? String(formattedNumber(adjusted.toFixed(2))) : undefined,
          };
        }
        return item;
      });

      // Adjust total substitutions so the "Total" in candidate email matches the adjusted facility bonus display.
      const travel = headerData?.assignment?.travel;
      const travelTotal =
        (travel?.arrivingTravel ?? 0) +
        (travel?.endingTravel ?? 0) +
        (travel?.interimTravel ?? 0) +
        (travel?.amnFlight ?? 0) +
        (travel?.amnRentalCar ?? 0);
      const pickListEntries = Array.from(adjustedPickListAmountsByLabel?.entries?.() ?? []) as Array<[string, number]>;
      const pickListAdjustedTotal = pickListEntries.reduce((sum, [label, amount]) => {
        if (label === BonusFields.facilityCompletion || label === BonusFields.facilitySignOn) {
          return sum + amount * 0.8;
        }
        return sum + amount;
      }, 0);
      const totalReimbursementAdjusted = travelTotal + pickListAdjustedTotal;

      templateSubstitutions = getSubstitutions(
        getSubstitutionValues(
          currentMarginData,
          headerData,
          existingRates,
          payPackageDetailsValues,
          bookingData?.brand,
        ),
      )?.map(token => {
        if (token?.input === '{{total}}') {
          return {
            ...token,
            value:
              totalReimbursementAdjusted > 0
                ? String(formattedNumber(totalReimbursementAdjusted.toFixed(2)))
                : undefined,
          };
        }
        if (token?.input === '{{totalReimbursement}}') {
          return {
            ...token,
            value: String(formattedNumber(totalReimbursementAdjusted.toFixed(2))),
          };
        }
        return token;
      });
      const tableData = {
        splits: {
          partialTemplateName: 'split',
          partialTemplate: splitTemplate,
          partialData: splitData,
        },
        reimbursements: {
          partialTemplateName: 'reimbursement',
          partialTemplate: reimbursementTemplate,
          partialData: adjustedReimbursementData,
        },
      };
      const previewRequestData = {
        body: payQuoteTemplate[0]?.body ? `${payQuoteTemplate[0]?.body}` : '',
        brand: user?.userInfo?.companyBrandName,
        signatureSubstitutions: signatureSubstitutionToken(user, userPreference),
        substitutions: templateSubstitutions.reduce((acc, item) => {
          if (item.name !== 'Signature') {
            acc[item.input.slice(2, -2)] = item.value || '';
          }
          return acc;
        }, {}),
        tableData: tableData,
        id: null,
        skipSignatureSubstitution: true,
      };

      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolPreview);
      body = yield call(TrackMarginToolPreview, applyDataToTemplate, previewRequestData);
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolPreview);
      useType = UseType.PayPackage;
      subject = getCandidateEmailSubject(currentMarginData);
    } else {
      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchplacementHeader);
      const placementHeaderResponse = yield call(
        TrackFetchplacementHeader,
        placementService.getConsolidatedPlacementDetails,
        {
          placementId: placementId,
          targetEndpoints: [ConsolidatedPlacementTargetEndpoints.HeaderDetails],
        },
      );
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchplacementHeader);

      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchOrderDetails);
      const orderData = yield call(TrackFetchOrderDetails, getOrderDetailsById, `${orderId}`);
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchOrderDetails);

      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchPlacementDocs);
      const placementDocsResponse = yield call(
        TrackFetchPlacementDocs,
        placementService.getPlacementDocuments,
        placementId,
      );
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchPlacementDocs);

      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchBillRateMod);
      const latestBillRate = yield call(
        TrackFetchBillRateMod,
        placementModService.getBillRateModLatestApproved,
        `${placementId}`,
      );
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchBillRateMod);

      const billRateMod = httpSuccess(latestBillRate?.status) && latestBillRate?.data;
      const template = templates?.filter(data => data.title === MarginTemplateTitle.ApprovalProcess);
      const placementHeaderData = placementHeaderResponse?.data?.header?.data;

      const candidateName = `${placementHeaderData?.candidate?.firstName} ${placementHeaderData?.candidate?.lastName}`;
      const recruiterName = `${currentMarginData?.recruiter?.firstName} ${currentMarginData?.recruiter?.lastName}`;
      const recruiterEmail = `${currentMarginData?.recruiter?.email}`;
      const accountManagerName = `${currentMarginData?.accountManager?.firstName} ${currentMarginData?.accountManager?.lastName}`;
      const accountManagerEmail = `${currentMarginData?.accountManager?.email}`;
      const latestSentDate = getLatestSentDateForClientConfirmation(placementDocsResponse?.data);
      const formattedLatestSentDate = moment(latestSentDate)?.isValid()
        ? moment(latestSentDate)?.format('MM/DD/YYYY hh:mm:ss a')
        : '';

      const sortedSplits = sortAssignmentSplits(headerData?.splits);
      const splitPartialData = getProcessedSplits(sortedSplits, billRateMod, headerData);

      const tableData = {
        splits: {
          partialTemplateName: 'split',
          partialData: splitPartialData,
          partialTemplate: approvalEmailSplitTemplate,
        },
      };

      const extensionOfPriorPlacementId = currentMarginData?.extensionOfPriorPlacementId;

      templateSubstitutions = getApprovalVariantEmailSubstitutions(
        currentMarginData,
        extensionOfPriorPlacementId,
        placementHeaderData,
        orderData,
        candidateName,
        recruiterName,
        recruiterEmail,
        accountManagerName,
        accountManagerEmail,
        formattedLatestSentDate,
        headerData,
        bookingData,
      );

      const payload = {
        body: template[0]?.body ? `${template[0]?.body}` : '',
        tableData: tableData,
        substitutions: templateSubstitutions.reduce((acc, item) => {
          if (item.name !== 'Signature') {
            acc[item.input.slice(2, -2)] = item.value || '';
          }
          return acc;
        }, {}),
        signatureSubstitutions: signatureSubstitutionToken(user.userInfo, userPreference),
        brand: user.userInfo?.companyBrandName,
        id: null,
        skipSignatureSubstitution: true,
      };

      subject = getApprovalEmailSubject(headerData, placementHeaderData);
      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolPreview);
      body = yield call(TrackMarginToolPreview, applyDataToTemplate, payload);
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolPreview);
    }

    const candidateDetails = getCandidateDetails(foundPlacement);
    const recipientList =
      isStatusUpdatedEmailModalOpen?.emailType === IEmailType.Manual
        ? formatManualRecipientList(
            statusId,
            selectEmailRecipients,
            sender,
            isStatusUpdatedEmailModalOpen?.emailVariant,
            candidateDetails,
          )
        : formatEmailRecipientsList(statusId - 1, selectEmailRecipients, sender, statusId);
    const notificationUser = searchNotificationData({ userInfo: user.userInfo });

    const marginCandidateDetails = {
      candidateEmail: foundPlacement?.header?.candidatePrimaryEmail,
      candidateId: foundPlacement?.header?.candidate?.id,
      candidateBrandId: foundPlacement?.header?.brandId,
      candidateBrand: foundPlacement?.header?.brandName,
    };

    const emailData = {
      useType,
      body,
      subject,
      sender,
      substitutions: templateSubstitutions,
      emailType: !isCandidateEmail ? SendType.bulk : SendType.one_to_one,
      sendType: !isCandidateEmail ? SendType.one_to_one : SendType.bulk,
      associatedRecords: [],
      tos: recipientList.to?.filter(value => value),
      ccs: recipientList.cc?.filter(value => value),
      bccs: recipientList.bcc?.filter(value => value),
      userRole: Concatenate(user.userInfo?.roles || [], ','),
      isInternal: !isCandidateEmail,
      initiationPoint: !isCandidateEmail
        ? InitiationPoints.marginToolInternalEmail
        : InitiationPoints.marginToolCandidateEmail,
      marginCandidateDetails,
    };

    if (isCandidateEmail) {
      emailData['useSubType'] = UseSubType.Ouote;
    }

    yield put(
      notificationDataActions.setEmailData({
        data: {
          ...EmailDefaultValues(),
          ...notificationUser,
          ...emailData,
          populateEmpListForMarginTool: isCandidateEmail,
          channel: ChannelType.email,
        },
        open: true,
        minimized: false,
      }),
    );
    yield put(
      placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({ openEmailModal: false, emailType: '' }),
    );
  } catch (error) {
    yield put(
      placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({ openEmailModal: false, emailType: '' }),
    );

    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolTemplate);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchplacementHeader);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchOrderDetails);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchPlacementDocs);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.fetchBillRateMod);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.marginToolPreview);

    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'handleMarginToolEmailCreation',
        area: 'src/store/redux-store/margin-tool/slices/margin-tool-email/margin-tool-email.saga.ts',
      },
    });
  }
}

export function* placementMarginToolEmailSaga() {
  yield takeLatest(
    placementMarginToolEmailAction.handlePlacementMarginToolEmailCreation.type,
    handlePlacementMarginToolEmailCreation,
  );
}
